import { useEffect } from "react";
import { scrollInputIntoView } from "../utils/scrollInputIntoView";

const KEYBOARD_OPEN_THRESHOLD = 80;
const MOBILE_MAX_WIDTH = 767;

const isMobileLayout = () =>
  window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;

const isFocusableInput = (target) => {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
    return false;
  }

  const type = String(target.type || "").toLowerCase();
  return type !== "hidden" && type !== "file" && type !== "checkbox" && type !== "radio";
};

const setMobileDocumentScrollLock = (keyboardOpen) => {
  if (!isMobileLayout()) return;

  document.documentElement.classList.toggle("keyboard-open", keyboardOpen);

  if (keyboardOpen) {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
    return;
  }

  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
};

const measureMobileNavOffset = () => {
  if (!isMobileLayout()) return "0px";

  const nav = document.querySelector(".mobile-bottom-nav");
  if (nav) {
    return `${Math.ceil(nav.getBoundingClientRect().height)}px`;
  }

  return "5rem";
};

const setAppHeight = () => {
  const viewport = window.visualViewport;
  const layoutHeight = window.innerHeight;
  const visibleHeight = viewport?.height ?? layoutHeight;
  const offsetTop = viewport?.offsetTop ?? 0;
  const bottomInset = viewport
    ? Math.max(0, layoutHeight - visibleHeight - offsetTop)
    : 0;
  const keyboardOpen = bottomInset > KEYBOARD_OPEN_THRESHOLD;

  document.documentElement.style.setProperty("--app-height", `${visibleHeight}px`);
  document.documentElement.style.setProperty("--vv-offset-top", `${offsetTop}px`);
  document.documentElement.style.setProperty("--vv-bottom-inset", `${bottomInset}px`);
  document.documentElement.style.setProperty(
    "--mobile-nav-offset",
    keyboardOpen ? "0px" : measureMobileNavOffset()
  );
  document.documentElement.style.setProperty(
    "--keyboard-open",
    keyboardOpen ? "1" : "0"
  );

  setMobileDocumentScrollLock(keyboardOpen);
};

const handleFocusIn = (event) => {
  if (!isMobileLayout() || !isFocusableInput(event.target)) return;
  scrollInputIntoView(event);
};

export default function useVisualViewportHeight() {
  useEffect(() => {
    setAppHeight();

    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", setAppHeight);
    viewport?.addEventListener("scroll", setAppHeight);
    window.addEventListener("resize", setAppHeight);
    window.addEventListener("orientationchange", setAppHeight);
    document.addEventListener("focusin", handleFocusIn);

    const nav = document.querySelector(".mobile-bottom-nav");
    const navObserver =
      nav && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(setAppHeight)
        : null;
    navObserver?.observe(nav);

    return () => {
      viewport?.removeEventListener("resize", setAppHeight);
      viewport?.removeEventListener("scroll", setAppHeight);
      window.removeEventListener("resize", setAppHeight);
      window.removeEventListener("orientationchange", setAppHeight);
      document.removeEventListener("focusin", handleFocusIn);
      navObserver?.disconnect();
      setMobileDocumentScrollLock(false);
    };
  }, []);
}
